/**
 * DS Toast — token-driven toast/snackbar primitive.
 *
 * Drop-in replacement for the subset of `react-hot-toast` used in this app:
 *   - `toast.success(msg, opts?)`
 *   - `toast.error(msg, opts?)`
 *   - `toast.loading(msg, opts?)` -> id
 *   - `toast.dismiss(id?)`
 *   - `<Toaster position? toastOptions? />` global mount
 *
 * Also exposes a typed React hook `useToast()` for in-component access.
 *
 * Visuals use `<InlineNotice>` body inside a positioned card, fully
 * token-driven (var(--bg-elev), var(--line), var(--sh-pop), var(--scrim)).
 * No hardcoded colors.
 */
import * as React from "react";
import { InlineNotice, type InlineNoticeTone } from "../display/InlineNotice";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToastTone = InlineNoticeTone; // info | warn | risk | ok
export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export interface ToastOptions {
  id?: string;
  /** Auto-dismiss in ms. Default 4000 (success: 3000, error: 4000, loading: Infinity). */
  duration?: number;
  /** Back-compat with react-hot-toast. Optional emoji / element shown next to the message. */
  icon?: React.ReactNode;
  /** Back-compat with react-hot-toast. Per-toast style overrides (currently ignored). */
  style?: React.CSSProperties;
}

export interface ToastRecord {
  id: string;
  tone: ToastTone | "loading";
  message: React.ReactNode;
  duration: number;
  icon?: React.ReactNode;
}

export interface ToasterProps {
  position?: ToastPosition;
  /** Accepted for back-compat with react-hot-toast. Mostly ignored. */
  toastOptions?: {
    duration?: number;
    style?: React.CSSProperties;
    success?: { duration?: number; iconTheme?: unknown };
    error?: { duration?: number; iconTheme?: unknown };
  };
}

// ---------------------------------------------------------------------------
// Pub/sub store (module singleton)
// ---------------------------------------------------------------------------

type Listener = (toasts: ToastRecord[]) => void;

const listeners = new Set<Listener>();
let toasts: ToastRecord[] = [];
let counter = 0;

function emit() {
  const snapshot = toasts.slice();
  listeners.forEach((l) => l(snapshot));
}

function subscribe(l: Listener): () => void {
  listeners.add(l);
  l(toasts.slice());
  return () => {
    listeners.delete(l);
  };
}

function add(record: ToastRecord) {
  // de-dup by id (replace existing)
  toasts = toasts.filter((t) => t.id !== record.id).concat(record);
  emit();
  if (record.duration !== Infinity && record.duration > 0) {
    setTimeout(() => dismiss(record.id), record.duration);
  }
}

function dismiss(id?: string) {
  if (id === undefined) {
    toasts = [];
  } else {
    toasts = toasts.filter((t) => t.id !== id);
  }
  emit();
}

function nextId(): string {
  counter += 1;
  return `t-${counter}`;
}

// ---------------------------------------------------------------------------
// Public `toast` API (default export)
// ---------------------------------------------------------------------------

function show(
  tone: ToastTone | "loading",
  message: React.ReactNode,
  opts?: ToastOptions,
): string {
  const id = opts?.id ?? nextId();
  let defaultDuration = 4000;
  if (tone === "ok") defaultDuration = 3000;
  else if (tone === "loading") defaultDuration = Infinity;
  add({
    id,
    tone,
    message,
    duration: opts?.duration ?? defaultDuration,
    icon: opts?.icon,
  });
  return id;
}

const toast = Object.assign(
  (message: React.ReactNode, opts?: ToastOptions) =>
    show("info", message, opts),
  {
    success: (message: React.ReactNode, opts?: ToastOptions) =>
      show("ok", message, opts),
    error: (message: React.ReactNode, opts?: ToastOptions) =>
      show("risk", message, opts),
    warn: (message: React.ReactNode, opts?: ToastOptions) =>
      show("warn", message, opts),
    info: (message: React.ReactNode, opts?: ToastOptions) =>
      show("info", message, opts),
    loading: (message: React.ReactNode, opts?: ToastOptions) =>
      show("loading", message, opts),
    dismiss: (id?: string) => dismiss(id),
    /** Generic show that picks a tone. */
    show,
  },
);

export { toast };
export default toast;

// ---------------------------------------------------------------------------
// useToast hook
// ---------------------------------------------------------------------------

export interface UseToastReturn {
  show: (args: {
    tone: ToastTone;
    message: React.ReactNode;
    duration?: number;
    id?: string;
  }) => string;
  dismiss: (id?: string) => void;
  toasts: ToastRecord[];
}

export function useToast(): UseToastReturn {
  const [list, setList] = React.useState<ToastRecord[]>(() => toasts.slice());
  React.useEffect(() => subscribe(setList), []);
  return React.useMemo(
    () => ({
      show: ({ tone, message, duration, id }) =>
        show(tone, message, { duration, id }),
      dismiss,
      toasts: list,
    }),
    [list],
  );
}

// ---------------------------------------------------------------------------
// <Toaster /> — global mount
// ---------------------------------------------------------------------------

const POSITION_STYLE: Record<ToastPosition, React.CSSProperties> = {
  "top-left": { top: 16, left: 16, alignItems: "flex-start" },
  "top-center": {
    top: 16,
    left: "50%",
    transform: "translateX(-50%)",
    alignItems: "center",
  },
  "top-right": { top: 16, right: 16, alignItems: "flex-end" },
  "bottom-left": { bottom: 16, left: 16, alignItems: "flex-start" },
  "bottom-center": {
    bottom: 16,
    left: "50%",
    transform: "translateX(-50%)",
    alignItems: "center",
  },
  "bottom-right": { bottom: 16, right: 16, alignItems: "flex-end" },
};

export function Toaster({
  position = "top-right",
}: ToasterProps): React.ReactElement {
  const [list, setList] = React.useState<ToastRecord[]>(() => toasts.slice());
  React.useEffect(() => subscribe(setList), []);

  // Escape closes the most recent toast
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && toasts.length > 0) {
        dismiss(toasts[toasts.length - 1].id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: "fixed",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        pointerEvents: "none",
        ...POSITION_STYLE[position],
      }}
    >
      {list.map((t) => (
        <ToastItem key={t.id} record={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({
  record,
  onDismiss,
}: {
  record: ToastRecord;
  onDismiss: () => void;
}): React.ReactElement {
  const tone: InlineNoticeTone =
    record.tone === "loading" ? "info" : record.tone;
  return (
    <div
      style={{
        pointerEvents: "auto",
        minWidth: 280,
        maxWidth: 480,
        background: "var(--bg-elev)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-md)",
        boxShadow: "var(--sh-pop)",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "stretch" }}>
        <div style={{ flex: 1, padding: 4 }}>
          <InlineNotice tone={tone}>
            {record.icon ? (
              <span style={{ marginRight: 8 }} aria-hidden>
                {record.icon}
              </span>
            ) : null}
            {record.message}
          </InlineNotice>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          style={{
            flexShrink: 0,
            border: "none",
            background: "transparent",
            color: "var(--ink-3)",
            cursor: "pointer",
            padding: "0 12px",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
